import { useSEO } from "@/hooks/useSEO";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  useSEO({ title: "Verify Your Email", description: "Confirm your Easy Drive Car Rental email address.", path: "/verify-email", noindex: true });
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const [purpose, setPurpose] = useState("account");
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token") || "";
    if (!token) {
      setState("fail");
      setError("This link is missing its verification token.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((r) => { setPurpose(r.data.purpose); setState("ok"); })
      .catch((e) => { setError(errMsg(e)); setState("fail"); });
  }, [params]);

  return (
    <div data-testid="verify-email-page" className="max-w-lg mx-auto px-6 pt-40 pb-24 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <img src="/logo.png" alt="Easy Drive Car Rental" className="h-20 w-auto object-contain mx-auto mb-8" />
        {state === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 size={40} className="mx-auto text-primary animate-spin" />
            <p className="mt-5 font-ui text-muted-foreground">Verifying your email…</p>
          </div>
        )}
        {state === "ok" && (
          <div data-testid="verify-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h1 className="mt-5 font-display text-4xl tracking-tight">
              {purpose === "account" ? <>Email <span className="italic gold-text">verified</span></> : <>Loyalty <span className="italic gold-text">activated</span></>}
            </h1>
            <p className="mt-4 text-muted-foreground font-ui leading-relaxed">
              {purpose === "account"
                ? "Your account is ready — sign in to manage bookings, track loyalty tiers and check out faster."
                : "Your Easy Drive Loyalty membership is now active. Every completed rental with this email moves you up a tier."}
            </p>
            <Link to={purpose === "account" ? "/login" : "/loyalty"} className="inline-block mt-8">
              <span data-testid="verify-continue-link" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8 py-3 inline-block transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
                {purpose === "account" ? "Sign In" : "View Loyalty Program"}
              </span>
            </Link>
          </div>
        )}
        {state === "fail" && (
          <div data-testid="verify-failed">
            <XCircle size={48} className="mx-auto text-destructive" />
            <h1 className="mt-5 font-display text-4xl tracking-tight">Link <span className="italic gold-text">expired</span></h1>
            <p className="mt-4 text-muted-foreground font-ui leading-relaxed">{error}</p>
            <p className="mt-3 text-sm text-muted-foreground font-ui">
              Register or join again to receive a fresh verification link — or WhatsApp us at +1 (242) 813-1775.
            </p>
            <Link to="/login" className="inline-block mt-8 gold-text font-ui hover:underline">Back to sign in →</Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
